import { clsx } from 'clsx';
import type { ReactNode } from 'react';

/**
 * Small read-only indicators from the kit: stat tiles, bar rows, money rows,
 * queue numbers, timers and result values.
 *
 * These are labels, not controls. Where one of them needs to be pressable the
 * answer is to wrap it in a Card with `onClick`, not to give the indicator a
 * handler — the same rule that keeps a StateChip from becoming a button.
 */

/** StatCard — a figure with its label (`maie.css` .statcard). */
export function StatCard({
  label,
  value,
  sub,
  tone,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  /**
   * Colours the FIGURE, not a badge beside it. The Accountant's refunds line is
   * the case: a negative number reads as negative because it is red and signed,
   * rather than because a separate chip says so.
   */
  tone?: 'danger' | 'success' | 'warn';
  className?: string;
}) {
  return (
    <div className={clsx('statcard', className)}>
      <p className="t-ovl mut">{label}</p>
      {/* `.sv` and `.sl`, not `t-title`/`t-cap`. The kit sizes a stat card's
          figure and its caption through its OWN classes and steps both up at
          desk width (20/26 -> 22/28); borrowing the generic type scale here
          silently drops that, and the card renders a size the design does not
          have. */}
      <p className={clsx('sv', tone === 'danger' && 'red', tone === 'success' && 'grn',
        tone === 'warn' && 'amb')} style={{ marginTop: 4 }}>
        {value}
      </p>
      {sub ? <p className="sl">{sub}</p> : null}
    </div>
  );
}

/**
 * BarRow — a labelled proportional bar (`maie.css` .barrow).
 *
 * `value` and `max` are given rather than a percentage so the bar and its
 * printed figure cannot disagree — the kit's reports draw both.
 */
export function BarRow({
  label,
  value,
  max,
  display,
  className,
}: {
  label: ReactNode;
  value: number;
  max: number;
  /** What to print. Defaults to `value`. */
  display?: ReactNode;
  className?: string;
}) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className={clsx('barrow', className)}>
      <span className="t-sec" style={{ minWidth: 92 }}>{label}</span>
      <span
        className="grow"
        style={{ background: 'var(--surface-100)', borderRadius: 'var(--r-pill)', height: 8 }}
        role="img"
        aria-label={`${pct}%`}
      >
        <span
          style={{
            display: 'block',
            width: `${pct}%`,
            height: '100%',
            background: 'var(--brand-500)',
            borderRadius: 'var(--r-pill)',
          }}
        />
      </span>
      <span className="t-sec w6" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {display ?? value}
      </span>
    </div>
  );
}

/** MoneyRow — a label and an amount on one baseline (`maie.css` .moneyrow). */
export function MoneyRow({
  label,
  amount,
  strong = false,
  className,
}: {
  label: ReactNode;
  amount: ReactNode;
  /** The total line. */
  strong?: boolean;
  className?: string;
}) {
  return (
    <div className={clsx('moneyrow', className)}>
      <span className={strong ? 't-strong' : 't-body'}>{label}</span>
      <span
        className={strong ? 't-strong' : 't-body'}
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {amount}
      </span>
    </div>
  );
}

/** QueueNum — a position in the day's queue (`maie.css` .queue-num). */
export function QueueNum({ n, className }: { n: ReactNode; className?: string }) {
  return <span className={clsx('queue-num', className)}>{n}</span>;
}

/**
 * TimerChip — a running clock on a task (`maie.css` .timer-chip).
 *
 * The nurse's mid-visit loop is the case it exists for: dilation is timed, and
 * the number is the point. `aria-live` is off by default — a chip that
 * announces every tick is worse than one that announces none.
 */
export function TimerChip({
  time,
  running = true,
  className,
}: {
  time: string;
  running?: boolean;
  className?: string;
}) {
  return (
    <span className={clsx('timer-chip', className)} data-running={running ? 'true' : 'false'}>
      {time}
    </span>
  );
}

/**
 * ResultValue — a lab figure carrying its own verdict
 * (`maie.css` .result-ok / .result-bad).
 *
 * "Abnormals speak" in the design: the value itself is coloured, rather than
 * relying on a separate flag the eye has to connect to it. Colour is never the
 * only signal — pass `flag` so the state is also readable as text.
 */
export function ResultValue({
  value,
  status = 'ok',
  flag,
  className,
}: {
  value: ReactNode;
  status?: 'ok' | 'bad';
  /** e.g. "H" or "critical" — the non-colour signal. */
  flag?: ReactNode;
  className?: string;
}) {
  return (
    <span className={clsx(status === 'bad' ? 'result-bad' : 'result-ok', 'w6', className)}>
      {value}
      {flag ? <span className="t-cap"> {flag}</span> : null}
    </span>
  );
}
